'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import ClockIcon from '@/components/icons/ClockIcon';
import { Button } from '@/components';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

type PublicCoupon = {
  id: string;
  code: string;
  description: string | null;
  discountType: 'percentage' | 'fixed' | string;
  discountValue: number;
  minOrderAmount: number | null;
  maxDiscount: number | null;
  validFrom: string | null;
  validUntil: string | null;
  applicableTo: string | null;
};

type DisplayCoupon = {
  id: string;
  discount: string;
  offerType: string;
  validUntil: string;
  description: string;
  code: string;
};

function formatDiscount(c: PublicCoupon): string {
  if (c.discountType === 'percentage') {
    return `${Math.round(c.discountValue)}%\nOFF`;
  }
  return `$${c.discountValue.toFixed(0)}\nOFF`;
}

function formatOfferType(applicableTo: string | null): string {
  switch ((applicableTo || 'all').toLowerCase()) {
    case 'pickup': return 'PICK-UP';
    case 'delivery': return 'DELIVERY';
    default: return 'ANY ORDER';
  }
}

function formatValidUntil(validUntil: string | null): string {
  if (!validUntil) return 'no expiry';
  const d = new Date(validUntil);
  if (Number.isNaN(d.getTime())) return 'no expiry';
  return `valid until ${d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}`;
}

function formatDescription(c: PublicCoupon): string {
  if (c.description) return c.description;
  const valueText =
    c.discountType === 'percentage' ? `${Math.round(c.discountValue)}% OFF` : `$${c.discountValue.toFixed(2)} OFF`;
  const minText = c.minOrderAmount ? ` on orders over $${c.minOrderAmount.toFixed(0)}` : '';
  return `Use code "${c.code}" at checkout for ${valueText}${minText}.`;
}

function mapToDisplay(c: PublicCoupon): DisplayCoupon {
  return {
    id: c.id,
    discount: formatDiscount(c),
    offerType: formatOfferType(c.applicableTo),
    validUntil: formatValidUntil(c.validUntil),
    description: formatDescription(c),
    code: c.code,
  };
}

function CouponCard({ coupon, onUse, justCopied }: { coupon: DisplayCoupon; onUse: () => void; justCopied: boolean }) {
  return (
    <div className="relative overflow-hidden">
      <span
        className="pointer-events-none absolute left-[calc(38%+1rem-11px)] top-[-12px] z-[2] hidden h-6 w-6 border border-neutral-10 rounded-full bg-neutral-5 sm:block"
        aria-hidden
      />
      <span
        className="pointer-events-none absolute left-[calc(38%+1rem-11px)] bottom-[-12px] z-[2] hidden h-6 w-6 border border-neutral-10 rounded-full bg-neutral-5 sm:block"
        aria-hidden
      />
      <article className="relative overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white p-3">
        <div
          className="pointer-events-none absolute inset-y-4 left-[calc(38%+1rem)] hidden border-l-2 border-dashed border-neutral-10 sm:block"
          aria-hidden
        />
        <div className="flex flex-col sm:grid sm:grid-cols-[38%_62%] sm:items-stretch">
          <div className="relative flex min-h-[176px] flex-col items-center justify-between rounded-xl bg-[#FED2D3] px-3 py-4 text-center text-blue-80">
            <div
              className="pointer-events-none absolute inset-0 opacity-25"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 18% 20%, #f6ccd3 2px, transparent 2px), radial-gradient(circle at 72% 38%, #f6ccd3 2px, transparent 2px), radial-gradient(circle at 36% 76%, #f6ccd3 2px, transparent 2px)',
                backgroundSize: '48px 48px',
              }}
              aria-hidden
            />
            <div className="relative -mt-2 inline-flex h-[68px] w-[88px] flex-col items-center justify-center self-end bg-blue-80 text-constant-white sm:h-[76px] sm:w-[96px]">
              <p className="whitespace-pre-line text-center text-[22px] font-extrabold leading-[0.92] tracking-[-0.02em] text-[#FED2D3] sm:text-2xl">
                {coupon.discount}
              </p>
            </div>
            <p
              className="relative w-full whitespace-nowrap text-[clamp(1.25rem,4.5cqi,2rem)] font-extrabold leading-none tracking-[0.04em]"
              style={{ containerType: 'inline-size' } as React.CSSProperties}
            >
              {coupon.offerType}
            </p>
          </div>

          <div className="relative flex min-h-[176px] flex-col px-1 pt-8 sm:border-t-0 sm:pl-6 sm:pr-1 sm:pt-3 sm:pb-3">
            <div
              className="pointer-events-none absolute inset-x-0 top-4 border-t-2 border-dashed border-neutral-10 sm:hidden"
              aria-hidden
            />
            <span
              className="pointer-events-none absolute -left-5 top-2 h-6 w-6 rounded-full border border-neutral-10 bg-neutral-5 sm:hidden"
              aria-hidden
            />
            <span
              className="pointer-events-none absolute -right-5 top-2 h-6 w-6 rounded-full border border-neutral-10 bg-neutral-5 sm:hidden"
              aria-hidden
            />
            <div className="inline-flex items-center gap-1 text-body-12-desktop font-medium text-neutral-40">
              <ClockIcon size={14} color="currentColor" className="shrink-0" aria-hidden />
              <span>{coupon.validUntil}</span>
            </div>

            <h2 className="mt-1 break-all font-mono text-lg font-bold leading-tight text-blue-100 sm:text-xl">
              {coupon.code}
            </h2>
            <p className="mt-1 line-clamp-2 text-sm font-medium leading-snug text-text-secondary sm:text-[15px]">
              {coupon.description}
            </p>

            <div className="mt-auto pt-2">
              <Button
                variant="tertiary"
                size="lg"
                onClick={onUse}
                className="w-full rounded-xl"
                aria-label={`Copy promo code ${coupon.code} and start shopping`}
              >
                {justCopied ? `Code copied, happy shopping!` : `Use promo code`}
              </Button>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

function EmptyCoupons() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 12% 18%, #1a2477 2px, transparent 2px), radial-gradient(circle at 78% 32%, #1a2477 2px, transparent 2px), radial-gradient(circle at 30% 78%, #1a2477 2px, transparent 2px), radial-gradient(circle at 68% 88%, #1a2477 2px, transparent 2px)',
          backgroundSize: '56px 56px',
        }}
        aria-hidden
      />

      <div className="relative grid grid-cols-1 items-center gap-6 px-6 py-10 sm:grid-cols-[auto_1fr] sm:gap-10 sm:px-10 sm:py-12">
        {/* Ghost coupon illustration, echoes the real coupon card visual language */}
        <div className="relative mx-auto sm:mx-0">
          <div className="relative h-[140px] w-[200px] sm:h-[156px] sm:w-[228px]">
            {/* Notch circles (mimics perforation on real card) */}
            <span
              className="pointer-events-none absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-neutral-5 ring-1 ring-neutral-10"
              aria-hidden
            />
            <span
              className="pointer-events-none absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-neutral-5 ring-1 ring-neutral-10"
              aria-hidden
            />
            <div className="absolute inset-0 rounded-2xl border-2 border-dashed border-neutral-10 bg-blue-5" aria-hidden />
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
              <svg
                className="h-9 w-9 text-blue-60"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 11V8a3 3 0 116 0v3M5 11h14a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8a1 1 0 011-1z" />
              </svg>
              <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-60">No deals yet</p>
            </div>
          </div>
          {/* Tiny stacked card behind, for depth */}
          <div
            className="pointer-events-none absolute -bottom-2 -right-2 -z-[1] h-[140px] w-[200px] rotate-[6deg] rounded-2xl border-2 border-dashed border-neutral-10 bg-neutral-5 sm:h-[156px] sm:w-[228px]"
            aria-hidden
          />
        </div>

        {/* Copy + CTAs */}
        <div className="text-center sm:text-left">
          <p className="text-h3-mobile font-bold leading-tight text-blue-100 sm:text-h3-desktop">
            No active coupons right now
          </p>
          <p className="mt-2 max-w-md text-body-16-desktop leading-relaxed text-text-secondary sm:mx-0 mx-auto">
            We&rsquo;re cooking up fresh deals. In the meantime, browse the menu, there&rsquo;s always good stuff in stock.
          </p>

          <div className="mt-5 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <Button asChild variant="primary" size="lg">
          <Link href="/search">
              Shop the menu
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
        </Button>
            <Button asChild variant="bordered" color="white" size="lg">
          <Link href="/">
              Back to home
            </Link>
        </Button>
          </div>

          <p className="mt-4 text-body-12-desktop text-text-tertiary">
            Tip: follow us on Instagram <span className="font-semibold text-blue-80">@dankcannabis</span>, that&rsquo;s where new drops show up first.
          </p>
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="relative overflow-hidden">
      <article className="relative overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white p-3">
        <div className="flex flex-col sm:grid sm:grid-cols-[40%_60%] sm:items-stretch sm:gap-4">
          <div className="min-h-[188px] animate-pulse rounded-xl bg-neutral-5" />
          <div className="flex min-h-[188px] flex-col gap-2 pt-4 sm:pt-0">
            <div className="h-3 w-24 animate-pulse rounded bg-neutral-5" />
            <div className="h-5 w-40 animate-pulse rounded bg-neutral-5" />
            <div className="h-3 w-full animate-pulse rounded bg-neutral-5" />
            <div className="mt-auto h-12 w-full animate-pulse rounded-xl bg-neutral-5" />
          </div>
        </div>
      </article>
    </div>
  );
}

export default function CouponsDealsSection() {
  const router = useRouter();
  const [coupons, setCoupons] = useState<DisplayCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/promo/public`, { cache: 'no-store' });
        if (!res.ok) throw new Error('Failed to load coupons');
        const data = await res.json();
        if (cancelled) return;
        const codes: PublicCoupon[] = Array.isArray(data?.codes) ? data.codes : [];
        setCoupons(codes.map(mapToDisplay));
      } catch {
        if (!cancelled) setCoupons([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleUse = async (coupon: DisplayCoupon) => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopiedId(coupon.id);
      setTimeout(() => setCopiedId((id) => (id === coupon.id ? null : id)), 1200);
    } catch {
      // Clipboard write may fail in restricted environments, still navigate.
    }
    router.push('/search');
  };

  return (
    <section className="w-full" aria-labelledby="coupons-listing-heading">
      <h2 id="coupons-listing-heading" className="sr-only">
        Available coupons
      </h2>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : coupons.length === 0 ? (
        <EmptyCoupons />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {coupons.map((coupon) => (
            <CouponCard
              key={coupon.id}
              coupon={coupon}
              justCopied={copiedId === coupon.id}
              onUse={() => void handleUse(coupon)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
